import React, { useEffect, useMemo, useRef, useState } from "react";
import { Row, Col, Table, Spin, Select, DatePicker, Form, Input } from "antd";
import { LeftOutlined, RightOutlined, SearchOutlined } from "@ant-design/icons";
import moment from "moment";
import dayjs from "dayjs";
import styled from "@emotion/styled";
import { utils, writeFileXLSX } from "xlsx";
// Redux
import { connect } from "react-redux";
import {
  getAllDepartmentList,
  getSelectedDepartmentId,
  getClientid,
  getUserList,
  getUserInfo,
  getLocations,
} from "redux/selectors";

import { CALL_API } from "common/API";
import { MONTH_LIST, STATUS_CODE } from "common/Constants";

import {
  addDaysinDate,
  dateInDetail,
  getKenyanDateTime,
  getMonthDateRange,
  getMySqlDate,
  momentDate,
  addMonthinDate,
} from "utils/Date";
import COLORS from "common/Colors";
import { find, get, includes, orderBy } from "lodash";
import { ButtonOutlined } from "reusableComponent/UIButtons";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";

const FIXED_COLUMN_WIDTHS = {
  fullname: 160,
  employeeid: 120,
  departmentname: 160,
  locationname: 140,
  totalHours: 110,
};
const DAY_COLUMN_WIDTH = 70;

const DURATION_TYPE_OPTIONS = [
  { value: 2, label: "Monthly Report" },
  { value: 3, label: "Weekly Report" },
  { value: 1, label: "Custom Date" },
];

const StyledTable = styled(Table)`
  // padding-top: 20px;
  .ant-table-content {
    overflow-x: auto;
    white-space: nowrap;
  }
  table {
    width: 100% !important;
  }
  .ant-table-cell {
    padding: 16px 12px;
  }
  .ant-table-thead .ant-table-cell {
    font-weight: bold;
    color: ${COLORS.SECONDARY_BLACK};
  }
  .non-white {
    background: #f9f9f9;
  }
  .total-hours-col {
    background: #fff4e5 !important;
    font-weight: 600;
  }
`;

// Uniform pill/box styling shared by every filter control (search input,
// department/location dropdowns, duration-type select, date pickers) so
// they all sit on one line with matching height, border and radius.
const FILTER_CONTROL_HEIGHT = "30px";
const FILTER_CONTROL_RADIUS = "25px";
const FILTER_CONTROL_BORDER = "#d9d9d9";

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  width: 100%;

  .ant-input-affix-wrapper,
  .ant-select-selector,
  .ant-picker {
    height: ${FILTER_CONTROL_HEIGHT} !important;
    border-radius: ${FILTER_CONTROL_RADIUS} !important;
    border-color: ${FILTER_CONTROL_BORDER} !important;
    display: flex;
    align-items: center;
  }

  .ant-select-selector .ant-select-selection-item,
  .ant-select-selector .ant-select-selection-placeholder {
    line-height: ${FILTER_CONTROL_HEIGHT} !important;
  }

  .ant-input-affix-wrapper {
    padding: 0 11px;
  }

  .ant-input-affix-wrapper:hover,
  .ant-select-selector:hover,
  .ant-picker:hover {
    border-color: ${COLORS.PRIMARY} !important;
  }

  .ant-input-affix-wrapper-focused,
  .ant-select-focused .ant-select-selector,
  .ant-picker-focused {
    border-color: ${COLORS.PRIMARY} !important;
    box-shadow: 0 0 0 2px rgba(45, 83, 218, 0.1) !important;
  }
`;

// display: contents makes the <form> itself invisible to the flex layout
// so its Form.Item children become direct flex items of FilterBar. Without
// this, the whole form acts as a single flex item and wraps as one block
// onto its own line instead of letting individual fields flow next to the
// search box on the same row.
const StyledApprovalSearchForm = styled(Form)`
  display: contents;

  .ant-form-item-with-help {
    margin-bottom: 0px;
    .ant-form-item-explain-connected {
      display: none;
    }
  }
  .ant-form-item {
    display: inline-block;
    margin-bottom: 0;
  }
  .outer-form {
    margin-right: 0px;
    display: contents;
  }
  .ant-select-selector {
    max-width: 180px;
    min-width: 180px;
    width: 180px;
  }
`;

const StyledButtonoutlined = styled(ButtonOutlined)`
  @media (max-width: 900px) {
    margin-left: -20px !important;
  }
  @media (max-width: 768px) {
    margin-left: -20px !important;
    margin-top: -1px !important;
  }
  @media (max-width: 575px) {
    margin-left: 0 !important;
    position: absolute;
    left: 0;
    margin-top: -1px;
  }
`;

const PeriodBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  background: #eef1fd;
  border-radius: 8px;
  padding: 10px 16px;
  margin-bottom: 16px;
  font-weight: 500;
  color: ${COLORS.SECONDARY_BLACK};

  button {
    border: none;
    background: transparent;
    cursor: pointer;
    color: ${COLORS.PRIMARY};
    font-size: 16px;
    display: flex;
    align-items: center;
  }
  button:disabled {
    color: #c7cbe0;
    cursor: not-allowed;
  }
`;

// ---------------------------------------------------------------------
// Original overtime rounding logic (restored from the legacy report).
// A record's overtime is: worked minutes - 9hr shift - break duration,
// then rounded up to the next full hour once minutes >= 50. Records
// with an "auto" checkout type never earn overtime.
// ---------------------------------------------------------------------

const isAutoCheckoutType = (record) => {
  const checkoutType = String(record?.checkouttype || "")
    .trim()
    .toLowerCase();
  return checkoutType === "auto";
};

// Initial Overtime (No Rounding) -> "HH:mm"
const getInitialOvertimeDuration = (record) => {
  const checkinDateTime = dayjs(
    `${dayjs(record.checkindate).format("YYYY-MM-DD")} ${record.checkintime}`,
    "YYYY-MM-DD HH:mm:ss"
  );

  const checkoutDateTime = dayjs(
    `${dayjs(record.checkoutdate).format("YYYY-MM-DD")} ${record.checkouttime}`,
    "YYYY-MM-DD HH:mm:ss"
  );

  // Total worked minutes
  const workedMinutes = checkoutDateTime.diff(checkinDateTime, "minute");

  // Break duration (HH:mm -> minutes)
  const breakDuration = record?.breakduration || "00:00";
  const [breakHours, breakMinutes] = breakDuration.split(":").map(Number);
  const totalBreakMinutes = (breakHours || 0) * 60 + (breakMinutes || 0);

  // Overtime = Worked - Shift Hours (9h) - Break Duration
  const overtimeMinutes = workedMinutes - 9 * 60 - totalBreakMinutes;

  if (overtimeMinutes <= 0) {
    return "00:00";
  }

  const hours = Math.floor(overtimeMinutes / 60);
  const minutes = overtimeMinutes % 60;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

// Final Overtime (50-minute rounding, auto-checkout excluded) -> "HH:mm"
const getFinalOvertimeDuration = (record) => {
  if (isAutoCheckoutType(record)) {
    return "00:00";
  }

  const initial = getInitialOvertimeDuration(record);

  if (initial === "00:00") {
    return "00:00";
  }

  const [hours, minutes] = initial.split(":").map(Number);
  const roundedHours = minutes >= 50 ? hours + 1 : hours;

  return `${String(roundedHours).padStart(2, "0")}:00`;
};

// "HH:mm" -> total minutes, for aggregation/display
const durationToMinutes = (duration) => {
  if (!duration) return 0;
  const [hours, minutes] = duration.split(":").map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};

const formatHours = (mins) => {
  if (!mins) return "0";
  return Number((mins / 60).toFixed(2)).toString();
};

function OvertimeReport({
  allDepartmentList,
  selectedDepartmentId,
  ClientId,
  userList,
  userInfo,
  locationList,
}) {
  const [form] = Form.useForm();
  const containerRef = useRef(null);
  const pageTitleRef = useRef(null);
  const [pageTitleHeight, setPageTitleHeight] = useState(0);
  const [scrollY, setScrollY] = useState();
  const [displayTableLoader, setDisplayTableLoader] = useState(false);
  const [overtimeList, setOvertimeList] = useState([]);
  const [monthList, setMonthList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [weekAnchor, setWeekAnchor] = useState(() => momentDate(getKenyanDateTime()));

  const duration_type = Form.useWatch("duration_type", form);
  const start_date = Form.useWatch("start_date", form);
  const end_date = Form.useWatch("end_date", form);
  const month = Form.useWatch("month", form);
  const departmentFilter = Form.useWatch("department", form) || [];
  const locationFilter = Form.useWatch("location", form) || [];

  useEffect(() => {
    const updatePageTitleHeight = () => {
      const h = pageTitleRef?.current?.offsetHeight || 0;
      setPageTitleHeight(h);
    };
    updatePageTitleHeight();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(updatePageTitleHeight);
      if (pageTitleRef.current) ro.observe(pageTitleRef.current);
    }
    window.addEventListener("resize", updatePageTitleHeight);
    return () => {
      window.removeEventListener("resize", updatePageTitleHeight);
      if (ro && pageTitleRef.current) ro.unobserve(pageTitleRef.current);
    };
  }, [pageTitleRef]);

  useEffect(() => {
    const calculateScrollY = () => {
      const dynamicHeight = Math.max(
        120,
        window.innerHeight - pageTitleHeight - 300
      );
      setScrollY(dynamicHeight);
    };
    calculateScrollY();
    window.addEventListener("resize", calculateScrollY);
    return () => window.removeEventListener("resize", calculateScrollY);
  }, [pageTitleHeight]);

  const curr_month = moment().month() + 1;

  const buildMonthList = () => {
    let month_list = [];
    for (let i = 0; i < curr_month; i++) {
      month_list.push(MONTH_LIST[i]);
    }
    setMonthList(month_list?.map((item) => ({ ...item, label: item?.label })));
  };

  // Populate the month options on mount -- this was previously left
  // disconnected, which is why the Month select showed "No data".
  useEffect(() => {
    buildMonthList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { startDateOfMonth, lastDateOfMonth } = useMemo(
    () =>
      getMonthDateRange(
        dateInDetail(getKenyanDateTime()).year,
        get(find(MONTH_LIST, { value: month }), "index", 0)
      ),
    [month]
  );

  const weekStart = moment(weekAnchor).startOf("week");
  const weekEnd = moment(weekAnchor).endOf("week");

  const periodStart =
    duration_type === 1 ? moment(start_date) : duration_type === 3 ? weekStart : startDateOfMonth;
  const periodEnd =
    duration_type === 1 ? moment(end_date) : duration_type === 3 ? weekEnd : lastDateOfMonth;
  const periodStartStr = periodStart?.isValid() ? periodStart.format("YYYY-MM-DD") : null;
  const periodEndStr = periodEnd?.isValid() ? periodEnd.format("YYYY-MM-DD") : null;

  const getOvertimeReport = async () => {
    setDisplayTableLoader(true);
    const { code, overtimeList: fetchedList = [] } = await CALL_API(
      `overtime-report/${ClientId}`,
      "post",
      {
        department: [""],
        start_date: getMySqlDate(periodStart),
        end_date: getMySqlDate(periodEnd),
        reportingmanager: userInfo?.userid,
      }
    );
    setDisplayTableLoader(false);
    if (includes([STATUS_CODE.SUCCESS, STATUS_CODE.RECORD_NOT_FOUND], code)) {
      setOvertimeList(orderBy(fetchedList, ["checkindatetime"], ["desc"]));
    }
  };

  useEffect(() => {
    if (!periodStartStr || !periodEndStr) return;
    getOvertimeReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDepartmentId, duration_type, periodStartStr, periodEndStr]);

  const validateDateRange = (date) => {
    const endDate = moment(form.getFieldValue("end_date"));
    if (endDate < date) {
      form.setFieldsValue({ end_date: date });
    }
  };

  const dayColumnKeys = useMemo(() => {
    if (!periodStartStr || !periodEndStr) return [];
    const days = [];
    const cursor = moment(periodStartStr);
    const last = moment(periodEndStr);
    while (cursor.isSameOrBefore(last, "day")) {
      days.push(cursor.format("YYYY-MM-DD"));
      cursor.add(1, "day");
    }
    return days;
  }, [periodStartStr, periodEndStr]);

  const pivotRows = useMemo(() => {
    const map = new Map();
    const visibleDays = new Set(dayColumnKeys);
    overtimeList.forEach((record) => {
      const uid = record?.userid;
      const dateKey = moment(record?.checkindate).format("YYYY-MM-DD");
      // The API can return punches outside the selected period (boundary
      // days / checkout-date matches). Those have no day column, so counting
      // them made Total Hours exceed the sum of the visible daily hours.
      if (!visibleDays.has(dateKey)) return;
      if (!map.has(uid)) {
        const user = userList?.find((item) => item?.userid === uid);
        const location = locationList?.find(
          (item) => item?.location_id === user?.baselocationid
        );
        map.set(uid, {
          userid: uid,
          employeeid: user?.employeeid || "-",
          fullname: record?.fullname,
          departmentid: String(record?.departmentid ?? ""),
          departmentname: record?.departmentname,
          locationid: user?.baselocationid || "",
          locationname: location?.location_name || "-",
          dayMinutes: {},
          totalMinutes: 0,
        });
      }
      const row = map.get(uid);

      // Use the original rounded overtime figure per punch record.
      const finalOvertime = getFinalOvertimeDuration(record);
      const minutes = durationToMinutes(finalOvertime);

      row.dayMinutes[dateKey] = (row.dayMinutes[dateKey] || 0) + minutes;
      row.totalMinutes += minutes;
    });
    return Array.from(map.values());
  }, [overtimeList, userList, locationList, dayColumnKeys]);

  const filteredRows = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    return pivotRows.filter((row) => {
      const matchesSearch =
        !search ||
        row.fullname?.toLowerCase().includes(search) ||
        row.employeeid?.toLowerCase?.().includes(search);
      const matchesDepartment =
        departmentFilter.length === 0 ||
        departmentFilter.map(String).includes(row.departmentid);
      const matchesLocation =
        locationFilter.length === 0 || locationFilter.includes(row.locationid);
      return matchesSearch && matchesDepartment && matchesLocation;
    });
  }, [pivotRows, searchText, departmentFilter, locationFilter]);

  const departmentOptions = useMemo(
    () =>
      (allDepartmentList || []).map((item) => ({
        label: item?.department_name,
        value: item?.department_id,
      })),
    [allDepartmentList]
  );

  const locationOptions = useMemo(
    () =>
      (locationList || []).map((item) => ({
        label: item?.location_name,
        value: item?.location_id,
      })),
    [locationList]
  );

  const columns = useMemo(() => {
    const fixedCols = [
      {
        title: "Name",
        dataIndex: "fullname",
        key: "fullname",
        fixed: "left",
        width: FIXED_COLUMN_WIDTHS.fullname,
      },
      {
        title: "Employee ID",
        dataIndex: "employeeid",
        key: "employeeid",
        fixed: "left",
        width: FIXED_COLUMN_WIDTHS.employeeid,
      },
      {
        title: "Department",
        dataIndex: "departmentname",
        key: "departmentname",
        fixed: "left",
        width: FIXED_COLUMN_WIDTHS.departmentname,
      },
      {
        title: "Location",
        dataIndex: "locationname",
        key: "locationname",
        fixed: "left",
        width: FIXED_COLUMN_WIDTHS.locationname,
      },
    ];

    const dayCols = dayColumnKeys.map((dateKey) => ({
      title: (
        <div style={{ textAlign: "center" }}>
          <div>{moment(dateKey).format("D")}</div>
          <div style={{ fontWeight: 400, fontSize: 11, color: "#999" }}>
            {moment(dateKey).format("ddd")}
          </div>
        </div>
      ),
      dataIndex: dateKey,
      key: dateKey,
      width: DAY_COLUMN_WIDTH,
      align: "center",
      render: (_, record) => formatHours(record.dayMinutes[dateKey]),
    }));

    const totalCol = {
      title: "Total Hours",
      dataIndex: "totalMinutes",
      key: "totalMinutes",
      fixed: "right",
      width: FIXED_COLUMN_WIDTHS.totalHours,
      align: "center",
      className: "total-hours-col",
      render: (_, record) => formatHours(record.totalMinutes),
    };

    return [...fixedCols, ...dayCols, totalCol];
  }, [dayColumnKeys]);

  const tableWidth =
    FIXED_COLUMN_WIDTHS.fullname +
    FIXED_COLUMN_WIDTHS.employeeid +
    FIXED_COLUMN_WIDTHS.departmentname +
    FIXED_COLUMN_WIDTHS.locationname +
    FIXED_COLUMN_WIDTHS.totalHours +
    dayColumnKeys.length * DAY_COLUMN_WIDTH;

  const periodLabel =
    duration_type === 1
      ? periodStart?.isValid() && periodEnd?.isValid()
        ? `${periodStart.format("Do MMM YYYY")} - ${periodEnd.format("Do MMM YYYY")}`
        : ""
      : duration_type === 3
      ? `${weekStart.format("Do MMM")} - ${weekEnd.format("Do MMM YYYY")}`
      : month
      ? `${month}, ${dateInDetail(getKenyanDateTime()).year}`
      : "";

  const monthIndex = monthList.findIndex((item) => item.value === month);
  const isPrevDisabled = duration_type === 2 && monthIndex <= 0;
  const isNextDisabled = duration_type === 2 && monthIndex >= monthList.length - 1;

  const handlePeriodShift = (direction) => {
    if (duration_type === 1) {
      if (!periodStart?.isValid() || !periodEnd?.isValid()) return;
      const span = periodEnd.diff(periodStart, "days") + 1;
      form.setFieldsValue({
        start_date: periodStart.clone().add(direction * span, "days"),
        end_date: periodEnd.clone().add(direction * span, "days"),
      });
    } else if (duration_type === 3) {
      setWeekAnchor((prev) => moment(prev).add(direction * 7, "days"));
    } else {
      const newIndex = monthIndex + direction;
      if (newIndex >= 0 && newIndex < monthList.length) {
        form.setFieldsValue({ month: monthList[newIndex].value });
      }
    }
  };

  const xport = () => {
    const elt = document.getElementById("OvertimeSummaryTable");
    const wb = utils.table_to_book(elt);
    writeFileXLSX(wb, "overtime-report.xlsx");
  };

  const paginationOptions = {
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
    pageSize: 100,
    showSizeChanger: false,
  };

  return (
    <>
      <Row gutter={16}>
        <Col xs={24} sm={24} md={12} className="align-left">
          <h6 className="pt-2">Overtime Summary</h6>
        </Col>
        <Col xs={24} sm={24} md={12} className="align-right">
          <StyledButtonoutlined onClick={xport} disabled={filteredRows.length === 0}>
            Export XLSX file
          </StyledButtonoutlined>
        </Col>
      </Row>
      <Row className="page-title mb-2" ref={pageTitleRef}>
        <Col xs={24} className="page-title-head" ref={containerRef}>
          <FilterBar>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 200 }}
            />
            <StyledApprovalSearchForm
              form={form}
              name="overtime_summary_form"
              layout="inline"
              initialValues={{
                duration_type: 3,
                month: dateInDetail(getKenyanDateTime()).monthName,
                start_date: momentDate(addMonthinDate(getKenyanDateTime(), -1)),
                end_date: momentDate(addDaysinDate(getKenyanDateTime())),
                department: [],
                location: [],
              }}
            >
              <span style={{ display: "inline-block", width: 180, marginTop: "15px"  }}>
                <DropdownCheckBox
                  name="department"
                  label="Department"
                  options={departmentOptions}
                  form={form}
                />
              </span>
              <span style={{ display: "inline-block", width: 180, marginTop: "15px"  }}>
                <DropdownCheckBox
                  name="location"
                  label="Location"
                  options={locationOptions}
                  form={form}
                />
              </span>
              <Form.Item name="duration_type" rules={[{ required: true, message: "" }]}>
                <Select
                  placeholder="Select..."
                  options={DURATION_TYPE_OPTIONS}
                  getPopupContainer={() => containerRef.current}
                />
              </Form.Item>
              <Form.Item className="outer-form" shouldUpdate>
                {() =>
                  duration_type === 1 ? (
                    <>
                      <Form.Item
                        name="start_date"
                        rules={[
                          () => ({
                            validator(rule, value) {
                              const date = moment(value);
                              return date.isValid() ? Promise.resolve() : Promise.reject();
                            },
                          }),
                        ]}
                      >
                        <DatePicker
                          placeholder="Start date"
                          onChange={validateDateRange}
                          allowClear={false}
                          getPopupContainer={() => containerRef.current}
                        />
                      </Form.Item>
                      <Form.Item
                        name="end_date"
                        rules={[
                          () => ({
                            validator(rule, value) {
                              const date = moment(value);
                              return date.isValid() ? Promise.resolve() : Promise.reject();
                            },
                          }),
                        ]}
                      >
                        <DatePicker
                          placeholder="End date"
                          onChange={validateDateRange}
                          allowClear={false}
                          getPopupContainer={() => containerRef.current}
                        />
                      </Form.Item>
                    </>
                  ) : duration_type === 3 ? null : (
                    <Form.Item name="month" rules={[{ required: true, message: "" }]}>
                      <Select
                        placeholder="Select..."
                        options={monthList}
                        getPopupContainer={() => containerRef.current}
                      />
                    </Form.Item>
                  )
                }
              </Form.Item>
            </StyledApprovalSearchForm>
          </FilterBar>
        </Col>
      </Row>
      <PeriodBar>
        <button type="button" onClick={() => handlePeriodShift(-1)} disabled={isPrevDisabled}>
          <LeftOutlined />
        </button>
        <span>{periodLabel}</span>
        <button type="button" onClick={() => handlePeriodShift(1)} disabled={isNextDisabled}>
          <RightOutlined />
        </button>
      </PeriodBar>
      <Spin spinning={displayTableLoader}>
        <StyledTable
          dataSource={filteredRows}
          columns={columns}
          rowClassName={(record, index) => (index % 2 === 0 ? "" : "non-white")}
          rowKey="userid"
          scroll={{ x: tableWidth, y: scrollY }}
          pagination={filteredRows.length > 100 ? paginationOptions : false}
        />
        <Table
          style={{ display: "none" }}
          dataSource={filteredRows}
          columns={columns}
          rowKey="userid"
          pagination={false}
          id="OvertimeSummaryTable"
        />
      </Spin>
    </>
  );
}

const mapStateToProps = (state) => ({
  allDepartmentList: getAllDepartmentList(state),
  selectedDepartmentId: getSelectedDepartmentId(state),
  ClientId: getClientid(state),
  userList: getUserList(state),
  userInfo: getUserInfo(state),
  locationList: getLocations(state),
});

export default connect(mapStateToProps, null)(OvertimeReport);